import React, { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "../../i18n/LanguageContext";
import "./css/ContactUsModal.css";
import { API_BASE_URL } from "../../config/apiConfig";

function ContactUsModal({ onClose }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Basic client-side sanitization - remove null bytes and control characters
    const sanitizedValue = value.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, "");
    setFormData((prev) => ({
      ...prev,
      [name]: sanitizedValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation with sanitized inputs
    const trimmedName = formData.name.trim();
    const trimmedEmail = formData.email.trim();
    const trimmedSubject = formData.subject.trim();
    const trimmedMessage = formData.message.trim();

    if (!trimmedName) {
      toast.error(t("contactUs.toast.nameRequired"));
      return;
    }
    if (trimmedName.length > 100) {
      toast.error(t("contactUs.toast.nameTooLong"));
      return;
    }
    if (!trimmedEmail) {
      toast.error(t("contactUs.toast.emailRequired"));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      toast.error(t("contactUs.toast.emailInvalid"));
      return;
    }
    if (trimmedEmail.length > 255) {
      toast.error(t("contactUs.toast.emailTooLong"));
      return;
    }
    if (!trimmedSubject) {
      toast.error(t("contactUs.toast.subjectRequired"));
      return;
    }
    if (trimmedSubject.length > 200) {
      toast.error(t("contactUs.toast.subjectTooLong"));
      return;
    }
    if (!trimmedMessage) {
      toast.error(t("contactUs.toast.messageRequired"));
      return;
    }
    if (trimmedMessage.length > 5000) {
      toast.error(t("contactUs.toast.messageTooLong"));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/base/contact`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: trimmedName,
            email: trimmedEmail,
            subject: trimmedSubject,
            message: trimmedMessage,
          }),
        },
      );

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || t("contactUs.toast.success"));
        onClose();
      } else {
        throw new Error(data.error || "Failed to send message");
      }
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error(
        error.message ||
          "Sorry, there was an error sending your message. Please try again or email us directly at support@kinprotect.com",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-modal-overlay" onClick={onClose}>
      <div
        className="contact-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="contact-modal-close" onClick={onClose}>
          ×
        </button>

        <h2>Contact Us</h2>
        <p className="contact-modal-subtitle">
          Have questions, suggestions, or feature requests? We'd love to hear
          from you.
        </p>

        <form onSubmit={handleSubmit} className="contact-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your full name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your.email@example.com"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="subject">Subject *</label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="What's this about?"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="message">Message *</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us more..."
              rows="5"
              required
            ></textarea>
          </div>

          <div className="contact-modal-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </button>
          </div>
        </form>

        <div className="contact-alternative">
          <p>Or reach us directly:</p>
          <p className="contact-email">
            <strong>Email:</strong>{" "}
            <a href="mailto:support@kinprotect.com">support@kinprotect.com</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ContactUsModal;
